import { onboardingDocumentListSchema, type OnboardingDocument } from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { API_BASE_URL, ApiRequestError, apiFetch } from '@/lib/api';

export function OnboardingTaskFiles({ taskId, canUpload }: { taskId: string; canUpload: boolean }) {
  const client = useQueryClient();
  const [pending, setPending] = useState(false);
  const documents = useQuery({
    queryKey: ['onboarding', 'task-documents', taskId],
    queryFn: () => apiFetch(`/onboarding/tasks/${taskId}/documents`, onboardingDocumentListSchema),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`${API_BASE_URL}/onboarding/tasks/${taskId}/documents`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      const json: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          json && typeof json === 'object' && 'message' in json && typeof json.message === 'string'
            ? json.message
            : 'Die Datei konnte nicht gespeichert werden.';
        throw new ApiRequestError({
          statusCode: response.status,
          code: 'UNKNOWN',
          message,
          timestamp: new Date().toISOString(),
          path: `/onboarding/tasks/${taskId}/documents`,
        });
      }
      return json;
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['onboarding', 'task-documents', taskId] });
      toast.success('Datei gespeichert.');
    },
  });

  async function onDownload(item: OnboardingDocument) {
    setPending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/onboarding/documents/${item.id}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Die Datei konnte nicht geöffnet werden.');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = item.fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Die Datei konnte nicht geöffnet werden.',
      );
    } finally {
      setPending(false);
    }
  }

  const ready = (documents.data?.items ?? []).some(
    (item) => item.scanStatus === 'CLEAN' || item.scanStatus === 'SKIPPED',
  );

  return (
    <div className="space-y-2">
      {documents.data?.items.map((item) => (
        <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span>
            {item.fileName}
            {item.scanStatus === 'PENDING' ? ' · wird geprüft' : ''}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={pending || item.scanStatus === 'PENDING'}
            onClick={() => void onDownload(item)}
          >
            Öffnen
          </Button>
        </div>
      ))}
      {canUpload ? (
        <label className="block text-sm">
          Datei
          <input
            className="mt-1 block w-full text-sm"
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) {
                return;
              }
              upload.mutate(file, {
                onError: (error) => {
                  toast.error(
                    error instanceof ApiRequestError
                      ? error.message
                      : 'Die Datei konnte nicht gespeichert werden.',
                  );
                },
              });
            }}
          />
        </label>
      ) : null}
      {canUpload && !ready ? (
        <p className="text-sm text-muted-foreground">
          Abschließen braucht eine freigegebene Datei.
        </p>
      ) : null}
    </div>
  );
}
